import React from 'react'
import { Card, Input, Space, Select, Dropdown, Button } from 'antd'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import { useOrders } from '../hooks/useOrders'
import { useOrderActions } from '../hooks/useOrderActions'
import { useExport } from '../../../shared/hooks/useExport'
import { OrderStatistics } from '../components/OrderStatistics'
import { OrdersTable } from '../components/OrdersTable'
import { OrderDetailsDrawer } from '../components/OrderDetailsDrawer'
import { BulkActionToolbar } from '../../../shared/components/BulkActionToolbar'
import { useAuthStore } from '../../../auth/store/auth-store'

export const OrdersScreen: React.FC = () => {
    const { orders, totalOrders, page, setPage, pageSize, setPageSize, searchText, setSearchText, statusFilter, setStatusFilter, statistics } = useOrders()
    const { hasPermission } = useAuthStore()
    const canCancel = hasPermission('orders.cancel')
    const { exportToCSV } = useExport()
    const {
        selectedOrder,
        isDetailDrawerOpen,
        handleRowClick,
        handleUpdateStatus,
        handleCancelOrder,
        handleBulkStatusUpdate,
        handleBulkCancel,
        closeDrawer,
    } = useOrderActions()

    const [selectedRowKeys, setSelectedRowKeys] = React.useState<React.Key[]>([])

    const handleSelectionChange = (newSelectedRowKeys: React.Key[]) => {
        setSelectedRowKeys(newSelectedRowKeys)
    }

    const handleClearSelection = () => {
        setSelectedRowKeys([])
    }

    const onBulkStatusUpdate = (status: string) => {
        handleBulkStatusUpdate(selectedRowKeys, status, () => setSelectedRowKeys([]))
    }

    const onBulkCancel = () => {
        handleBulkCancel(selectedRowKeys, () => setSelectedRowKeys([]))
    }

    const statusMenu = [
        { key: 'confirmed', label: 'Mark as Confirmed' },
        { key: 'packed', label: 'Mark as Packed' },
        { key: 'shipped', label: 'Mark as Shipped' },
        { key: 'delivered', label: 'Mark as Delivered' },
    ]

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <OrderStatistics statistics={statistics} />

            <Card>
                <Space style={{ marginBottom: 16 }}>
                    <Input
                        placeholder="Search orders..."
                        prefix={<SearchOutlined />}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{ width: 300 }}
                    />
                    <Select
                        placeholder="Filter by status"
                        style={{ width: 150 }}
                        value={statusFilter}
                        onChange={setStatusFilter}
                    >
                        <Select.Option value="all">All Status</Select.Option>
                        <Select.Option value="confirmed">Confirmed</Select.Option>
                        <Select.Option value="packed">Packed</Select.Option>
                        <Select.Option value="shipped">Shipped</Select.Option>
                        <Select.Option value="delivered">Delivered</Select.Option>
                        <Select.Option value="cancelled">Cancelled</Select.Option>
                    </Select>
                    <Button icon={<DownloadOutlined />} onClick={() => exportToCSV(orders, 'orders-export')}>
                        Export
                    </Button>
                </Space>

                <OrdersTable
                    orders={orders}
                    totalOrders={totalOrders}
                    currentPage={page}
                    pageSize={pageSize}
                    onPageChange={(p, ps) => {
                        setPage(p)
                        setPageSize(ps)
                    }}
                    onRowClick={handleRowClick}
                    selectedRowKeys={selectedRowKeys}
                    onSelectionChange={handleSelectionChange}
                />
            </Card>

            <BulkActionToolbar
                selectedCount={selectedRowKeys.length}
                onClearSelection={handleClearSelection}
                actions={
                    <Space>
                        <Dropdown
                            menu={{
                                items: statusMenu,
                                onClick: ({ key }) => onBulkStatusUpdate(key)
                            }}
                        >
                            <Button>Update Status</Button>
                        </Dropdown>
                        {canCancel && <Button danger onClick={onBulkCancel}>Cancel Selected</Button>}
                    </Space>
                }
            />

            <OrderDetailsDrawer
                order={selectedOrder}
                open={isDetailDrawerOpen}
                onClose={closeDrawer}
                onUpdateStatus={handleUpdateStatus}
                onCancelOrder={handleCancelOrder}
                canCancel={canCancel}
            />
        </Space>
    )
}
